import type { AmbientEvaluatorInput, AmbientOpportunity, AmbientKind } from './ambientTypes'
import { generateContentWithFallback } from '../../geminiClient'
import { lineFeatureFlags } from '../../../config/lineFeatureFlags'

const REFINE_COST_CR = 4
const RATE_WINDOW_MS = 5 * 60 * 1000
const IDLE_MS = 45 * 1000

interface RefineState {
  lastCallAt: number
}
const state: RefineState = { lastCallAt: 0 }

export interface AmbientLLMRefineDeps {
  /** Live spendable Cr balance (from GamificationContext.ledgerCredits). */
  ledgerCredits: number
  /**
   * Server-side burn hook. Wire to a callable that atomically debits the
   * user's personal wallet by `amount` and returns the new balance. If the
   * callable throws (rules deny / insufficient), the LLM refine skips.
   * When omitted, the LLM refine is disabled — we never present LLM chips
   * without a matching deduction path.
   */
  burnCredits?: (amount: number, reason: string) => Promise<void>
  /** Milliseconds since the user's last keystroke; used for the idle gate. */
  msSinceLastKeystroke: number
  /** Signal to abort in-flight requests when the user starts typing. */
  signal?: AbortSignal
}

const gatesPass = (input: AmbientEvaluatorInput, heuristicCount: number, deps: AmbientLLMRefineDeps): boolean => {
  if (!lineFeatureFlags.ambientLLM) return false
  if (!deps.burnCredits) return false
  if (deps.ledgerCredits < REFINE_COST_CR) return false
  if (heuristicCount > 0) return false
  if (deps.msSinceLastKeystroke < IDLE_MS) return false
  if (input.now - state.lastCallAt < RATE_WINDOW_MS) return false
  return true
}

const buildDigest = (input: AmbientEvaluatorInput): string => {
  const signalLine = input.signals.slice(-5).map((signal) => `- ${signal.kind} @ ${new Date(signal.timestamp).toISOString().slice(11, 16)}`).join('\n')
  const elementLine = input.workspaceElements.slice(0, 12).map((element) => `- ${element.type} "${element.title}" (updated ${element.updatedAtIso ?? 'never'})`).join('\n')
  return `Recent signals:\n${signalLine || '(none)'}\n\nActive workspace nodes:\n${elementLine || '(none)'}`
}

interface RefineLLMPayload {
  opportunities: Array<{
    kind: AmbientKind
    title: string
    reason: string
    prompt: string
    modifyPrompt?: string
    targetId?: string
    score?: number
  }>
}

const parsePayload = (raw: string): RefineLLMPayload | null => {
  const start = raw.indexOf('{')
  const end = raw.lastIndexOf('}')
  if (start < 0 || end <= start) return null
  try {
    const parsed = JSON.parse(raw.slice(start, end + 1)) as RefineLLMPayload
    if (!Array.isArray(parsed.opportunities)) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Optional LLM refinement pass. Only runs when the heuristic layer produced
 * nothing recently AND the user has been idle AND the ambientLLM flag is on
 * AND the burnCredits dep is wired AND the ledger has enough Cr. Deducts on
 * successful display through the injected burnCredits callable.
 */
export const refineOpportunitiesWithLLM = async (
  input: AmbientEvaluatorInput,
  heuristicOpportunities: AmbientOpportunity[],
  deps: AmbientLLMRefineDeps,
): Promise<AmbientOpportunity[]> => {
  if (!gatesPass(input, heuristicOpportunities.length, deps)) return []

  state.lastCallAt = input.now
  const prompt = `You are Line's ambient copilot. Study the recent workspace signals below and propose at most one high-value proactive suggestion. Return ONLY JSON:
{
  "opportunities": [{
    "kind": "AC_DRAFT" | "DEDUPE" | "FOCUS_SYNC" | "STALE_BOSS" | "REJECTED_PIVOT" | "CHECKLIST_CLOSE" | "UPLOAD_LINK",
    "title": string,
    "reason": string,
    "prompt": string,
    "modifyPrompt"?: string,
    "targetId"?: string,
    "score"?: number
  }]
}

Rules:
- Return {"opportunities": []} if nothing is genuinely useful right now.
- "title" is 4-6 words, action-oriented ("Draft acceptance criteria?").
- "reason" is one sentence citing a concrete recent signal.
- "prompt" is the exact text that will pre-fill the user's chat if accepted.

${buildDigest(input)}`

  try {
    const response = await generateContentWithFallback({
      model: 'gemini-2.5-flash',
      payload: prompt,
      signal: deps.signal,
      generationConfig: {
        responseMimeType: 'application/json',
      },
    })
    const parsed = parsePayload(response.text)
    if (!parsed?.opportunities.length) return []
    await deps.burnCredits!(REFINE_COST_CR, 'line_ambient_refine')
    return parsed.opportunities.slice(0, 1).map((entry, idx) => {
      const targetElement = entry.targetId ? input.workspaceElements.find((el) => el.id === entry.targetId) : undefined
      return {
        id: `LLM:${entry.kind}:${entry.targetId ?? 'global'}:${Math.floor(input.now / (60 * 60 * 1000))}`,
        kind: entry.kind,
        title: entry.title.slice(0, 80),
        reason: entry.reason.slice(0, 200),
        primaryAction: { label: 'Accept', prompt: entry.prompt },
        secondaryAction: entry.modifyPrompt ? { label: 'Modify', prompt: entry.modifyPrompt } : undefined,
        targetRefs: targetElement ? [{ id: targetElement.id, type: targetElement.type, title: targetElement.title, board: targetElement.board }] : [],
        score: Math.min(1, Math.max(0.5, entry.score ?? 0.6)) + (idx === 0 ? 0.05 : 0),
        expiresAt: input.now + 10 * 60 * 1000,
        fromLLM: true,
      }
    })
  } catch (error) {
    console.info('[ambient][llm] refine skipped', error)
    return []
  }
}
